import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  Plus,
  FunnelSimple,
  CircleNotch,
  MapPin,
  Clock,
  User,
  CheckCircle,
  Warning,
  CalendarBlank
} from '@phosphor-icons/react';


const STATUS_CONFIG = {
  'scheduled': { label: 'Programada', color: 'bg-blue-100 text-blue-800' },
  'in_progress': { label: 'En Progreso', color: 'bg-yellow-100 text-yellow-800' },
  'completed': { label: 'Completada', color: 'bg-green-100 text-green-800' },
  'cancelled': { label: 'Cancelada', color: 'bg-red-100 text-red-800' },
};

const PRIORITY_CONFIG = {
  'low': { label: 'Baja', color: 'bg-slate-100 text-slate-600' },
  'normal': { label: 'Normal', color: 'bg-blue-100 text-blue-700' },
  'high': { label: 'Alta', color: 'bg-orange-100 text-orange-700' },
  'urgent': { label: 'Urgente', color: 'bg-red-100 text-red-700' },
};

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG['scheduled'];
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${config.color}`}>
      {config.label}
    </span>
  );
}

function PriorityBadge({ priority }) {
  const config = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG['normal'];
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
}

export default function WorkOrdersPage() {
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchWorkOrders();
  }, [statusFilter]);

  const fetchWorkOrders = async () => {
    try {
      setLoading(true);
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const response = await axios.get(`${API_URL}/api/work-orders${params}`, {
        withCredentials: true
      });
      setWorkOrders(response.data);
    } catch (error) {
      console.error('Error fetching work orders:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="work-orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Órdenes de Trabajo</h1>
          <p className="text-slate-500">Gestiona las órdenes de trabajo y asignaciones</p>
        </div>
        <Link
          to="/work-orders/new"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          data-testid="new-work-order-button"
        >
          <Plus size={20} weight="bold" />
          Nueva Orden
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center gap-4">
          <FunnelSimple size={20} className="text-slate-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            data-testid="status-filter"
          >
            <option value="">Todos los estados</option>
            <option value="scheduled">Programada</option>
            <option value="in_progress">En Progreso</option>
            <option value="completed">Completada</option>
            <option value="cancelled">Cancelada</option>
          </select>
        </div>
      </div>

      {/* Work Orders List */}
      <div className="grid gap-4">
        {loading ? (
          <div className="flex items-center justify-center py-16 bg-white rounded-xl border border-slate-200">
            <CircleNotch size={32} className="animate-spin text-blue-600" />
          </div>
        ) : workOrders.length > 0 ? (
          workOrders.map((wo) => (
            <div
              key={wo.id}
              onClick={() => navigate(`/work-orders/${wo.id}`)}
              className="bg-white rounded-xl border border-slate-200 p-5 cursor-pointer hover:shadow-md transition-shadow"
              data-testid={`work-order-${wo.id}`}
            >
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-bold text-blue-600">{wo.work_order_number}</span>
                    <StatusBadge status={wo.status} />
                    <PriorityBadge priority={wo.priority} />
                  </div>
                  <h3 className="font-semibold text-lg text-slate-900 mb-1">{wo.title}</h3>
                  <p className="text-slate-600">{wo.client_name}</p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4 text-sm text-slate-500">
                  <div className="flex items-center gap-2">
                    <CalendarBlank size={16} />
                    <span>{new Date(wo.scheduled_date).toLocaleDateString('es-ES')}</span>
                    {wo.scheduled_time && <span>• {wo.scheduled_time}</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin size={16} />
                    <span>{wo.project_city || wo.project_address}</span>
                  </div>
                  {wo.technician_names?.length > 0 && (
                    <div className="flex items-center gap-2">
                      <User size={16} />
                      <span>{wo.technician_names.join(', ')}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Progress bar for checklist */}
              {wo.checklist?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-slate-500">Progreso del checklist</span>
                    <span className="font-medium">
                      {wo.checklist.filter(i => i.completed).length} / {wo.checklist.length}
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div 
                      className="bg-green-500 h-2 rounded-full transition-all"
                      style={{ 
                        width: `${(wo.checklist.filter(i => i.completed).length / wo.checklist.length) * 100}%` 
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 py-16 text-center">
            <p className="text-slate-500 mb-4">No hay órdenes de trabajo</p>
            <Link
              to="/work-orders/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus size={18} />
              Crear primera orden
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
